import { ErrorHandler, Injectable, NgZone, OnDestroy, inject } from '@angular/core';
import { HttpBackend, HttpClient } from '@angular/common/http';
import { Subject, firstValueFrom, takeUntil, timeout } from 'rxjs';
import Cookies from 'js-cookie';
import { AuthStateService } from './auth-state.service';
import { environment } from '../../../environments/environment';

const MAX_BYTES = 14 * 1024;
const SECRET_KEY = /authorization|cookie|csrf|password|qr.?code|secret|token/i;

export function createRequestId(): string {
  const values = new Uint8Array(16);
  try { crypto.getRandomValues(values); }
  catch { for (let i = 0; i < values.length; i++) values[i] = Math.floor(Math.random() * 256); }
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789_-';
  return Array.from(values, value => chars[value & 63]).join('');
}

export function sanitizeLogText(value: unknown, max = 1000): string | undefined {
  if (typeof value !== 'string') return undefined;
  return value
    .replace(/(\/reset-password\/)[^/?#\s)'"<>]+/gi, '$1[REDACTED]')
    .replace(/\beyJ[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\.[A-Za-z0-9_-]{8,}\b/g, '[REDACTED_JWT]')
    .replace(/(https?:\/\/[^\s?#)]+)[?#][^\s)]+/gi, '$1?[REDACTED]')
    .replace(/(["']?(?:authorization|cookie|csrf(?:_token)?|password|qr.?code|secret|(?:access_|refresh_)?token)["']?\s*[:=]\s*)"(?:\\[\s\S]|[^"\\])*"/gi, '$1[REDACTED]')
    .replace(/(["']?(?:authorization|cookie|csrf(?:_token)?|password|qr.?code|secret|(?:access_|refresh_)?token)["']?\s*[:=]\s*)'(?:\\[\s\S]|[^'\\])*'/gi, '$1[REDACTED]')
    .replace(/(["']?(?:authorization|cookie|csrf(?:_token)?|password|qr.?code|secret|(?:access_|refresh_)?token)["']?\s*[:=]\s*)["'][^\r\n]*/gi, '$1[REDACTED]')
    .replace(/(["']?(?:authorization|cookie|csrf(?:_token)?|password|qr.?code|secret|(?:access_|refresh_)?token)["']?\s*[:=]\s*)(?:Bearer\s+)?[^"'\s,;}]+/gi, '$1[REDACTED]')
    .replace(/\bBearer\s+[A-Za-z0-9._~+/=-]+/gi, 'Bearer [REDACTED]').slice(0, max);
}

export function sanitizeLogValue(value: any, depth = 0): any {
  if (value === null || typeof value === 'boolean') return value;
  if (typeof value === 'string') return sanitizeLogText(value);
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (depth >= 4) return '[TRUNCATED]';
  if (Array.isArray(value)) return value.slice(0, 20).map(item => sanitizeLogValue(item, depth + 1));
  if (!value || typeof value !== 'object') return undefined;
  return Object.keys(value).slice(0, 30).reduce((result: any, key) => {
    if (!['__proto__', 'constructor', 'prototype'].includes(key)) {
      result[key.slice(0, 80)] = SECRET_KEY.test(key) ? '[REDACTED]' : sanitizeLogValue(value[key], depth + 1);
    }
    return result;
  }, {});
}

function stripUrl(value: any): string | undefined {
  return typeof value === 'string' ? sanitizeLogText(value.split(/[?#]/)[0], 500) : undefined;
}

function normalizeError(error: any): any {
  return {
    name: sanitizeLogText(error?.name, 120) || 'Error',
    message: sanitizeLogText(error?.message || (typeof error === 'string' ? error : 'Unknown error')),
    stack: sanitizeLogText(error?.stack, 6000), fileName: stripUrl(error?.fileName),
    lineNumber: Number.isFinite(error?.lineNumber) ? error.lineNumber : undefined,
    columnNumber: Number.isFinite(error?.columnNumber) ? error.columnNumber : undefined,
  };
}

function getBuildVersion(): string {
  for (const script of Array.from(document.scripts)) {
    const src = script.getAttribute('src');
    if (!src) continue;
    const url = new URL(src, document.baseURI);
    if (/\/main(?:[.-][^/]+)?\.js$/.test(url.pathname)) {
      return sanitizeLogText(url.searchParams.get('v') || url.pathname.split('/').pop(), 120) || 'source';
    }
  }
  return 'unknown';
}

@Injectable({ providedIn: 'root' })
export class FrontLogService implements OnDestroy {
  // Log delivery must not refresh credentials, navigate, or generate another logged error.
  private http = new HttpClient(inject(HttpBackend));
  private auth = inject(AuthStateService);
  private zone = inject(NgZone);
  private destroyed$ = new Subject<void>();
  private destroyed = false;
  private pending: any[] = [];
  private breadcrumbs: any[] = [];
  private recentErrors = new Map<string, number>();
  private pageSessionId = createRequestId();
  private count = 0;
  private flushing = false;
  private installed = false;
  private timer?: ReturnType<typeof setTimeout>;
  private authSubscription = this.auth.tokenChanges.subscribe(token => { if (token) this.schedule(0); });

  event(event: string, context?: any): void { this.record('info', event, undefined, context); }
  warn(event: string, message: string, context?: any): void { this.record('warn', event, message, context); }
  captureException(event: string, error: any, context?: any): void {
    try { this.record('error', event, normalizeError(error).message, { ...sanitizeLogValue(context), breadcrumbs: this.breadcrumbs.slice(-10) }, normalizeError(error)); }
    catch { /* Diagnostics must never break the application. */ }
  }

  private record(level: string, event: string, message?: string, context?: any, error?: any): void {
    try {
      if (this.destroyed || this.count >= 100) return;
      const fingerprint = [event, message, error?.stack, context?.requestId].join('|');
      if (level === 'error') {
        const last = this.recentErrors.get(fingerprint);
        this.recentErrors.set(fingerprint, performance.now());
        if (last !== undefined && performance.now() - last < 60000) return;
      }
      const safeContext = sanitizeLogValue(context);
      this.breadcrumbs.push({ timestamp: new Date().toISOString(), event: event.slice(0, 80), context: safeContext });
      if (this.breadcrumbs.length > 25) this.breadcrumbs.shift();
      const payload: any = {
        level, event, clientTimestamp: new Date().toISOString(), pageSessionId: this.pageSessionId, sequence: ++this.count,
        requestId: typeof context?.requestId === 'string' ? context.requestId.slice(0, 64) : undefined,
        route: stripUrl(location.pathname),
        buildVersion: getBuildVersion(),
        message: sanitizeLogText(message), error,
        context: { online: navigator.onLine, visibility: document.visibilityState, viewportWidth: innerWidth, viewportHeight: innerHeight, ...safeContext },
      };
      if (this.bytes(payload) > MAX_BYTES - 32) {
        payload.message = sanitizeLogText(payload.message, 500);
        if (payload.error) payload.error = { ...payload.error, message: sanitizeLogText(payload.error.message, 500), stack: sanitizeLogText(payload.error.stack, 2000) };
        payload.context = { truncated: true, stage: safeContext?.stage, entry: safeContext?.entry, elapsedMs: safeContext?.elapsedMs, online: navigator.onLine };
      }
      // A pathological event must not make the entire API batch exceed its 16 KB limit.
      if (this.bytes(payload) > MAX_BYTES - 32) { delete payload.error?.stack; payload.context = { truncated: true }; }
      this.pending.push(payload);
      this.schedule(100);
    } catch { /* Logging is best effort. */ }
  }

  private bytes(value: any): number { return new TextEncoder().encode(JSON.stringify(value)).length; }

  private schedule(delay: number): void {
    if (this.destroyed || this.timer !== undefined || this.flushing || !this.pending.length) return;
    this.timer = this.zone.runOutsideAngular(() => setTimeout(() => { this.timer = undefined; void this.flush(); }, delay));
  }

  async flush(): Promise<void> {
    if (this.destroyed || this.flushing || !this.auth.token || !this.pending.length) return;
    this.flushing = true;
    try {
      const events: any[] = [];
      while (this.pending.length && events.length < 20) {
        if (events.length && this.bytes({ events: [...events, this.pending[0]] }) > MAX_BYTES) break;
        events.push(this.pending.shift());
      }
      const headers: Record<string, string> = { Authorization: `Bearer ${this.auth.token}` };
      const csrf = Cookies.get('csrf_token');
      if (csrf) headers['X-CSRF-Token'] = csrf;
      await firstValueFrom(this.http.post(`${environment.apiBaseUrl}/client-log`, { events }, { headers, withCredentials: true }).pipe(timeout(2000), takeUntil(this.destroyed$)));
    } catch { /* Never affect QR, authentication, or navigation on a logging failure. */ }
    finally { this.flushing = false; this.schedule(100); }
  }

  installGlobalHandlers(): void {
    if (this.installed) return;
    this.installed = true;
    const early = window as any;
    if (early.__sfFrontErrorHandlers) {
      window.removeEventListener('error', early.__sfFrontErrorHandlers.error, true);
      window.removeEventListener('unhandledrejection', early.__sfFrontErrorHandlers.rejection);
    }
    const queue = Array.isArray(early.__sfFrontErrorQueue) ? early.__sfFrontErrorQueue.slice(0, 20) : [];
    early.__sfFrontErrorQueue = [];
    early.__sfFrontErrorHandlers = null;
    for (const item of queue) {
      if (item.type === 'resource') this.resourceError(item.tagName, item.resourceUrl);
      else this.captureException(item.type === 'rejection' ? 'app.unhandled_rejection' : 'app.window_error', item.error, { early: true });
    }
    this.zone.runOutsideAngular(() => {
      window.addEventListener('error', this.onError, true);
      window.addEventListener('unhandledrejection', this.onRejection);
    });
  }

  private resourceError(tagName: string, url: string): void {
    this.record('error', 'app.resource_load_failed', 'Resource failed to load', { tagName, resourceUrl: stripUrl(url) });
  }
  private onError = (event: ErrorEvent): void => {
    const target = event.target as any;
    if (target && target !== window && (target.src || target.href)) this.resourceError(target.tagName, target.src || target.href);
    else this.captureException('app.window_error', event.error || event.message, { fileName: stripUrl(event.filename), lineNumber: event.lineno, columnNumber: event.colno });
  };
  private onRejection = (event: PromiseRejectionEvent): void => { this.captureException('app.unhandled_rejection', event.reason); };

  ngOnDestroy(): void {
    this.destroyed = true;
    clearTimeout(this.timer);
    this.authSubscription.unsubscribe();
    window.removeEventListener('error', this.onError, true);
    window.removeEventListener('unhandledrejection', this.onRejection);
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}

@Injectable()
export class FrontLogErrorHandler extends ErrorHandler {
  private log = inject(FrontLogService);
  override handleError(error: any): void {
    super.handleError(error);
    this.log.captureException('app.angular_exception', error?.rejection ?? error);
  }
}
